import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { FaSlack } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';

import { AutomationRateBar } from './AutomationRateBar';
import { computeDashboardMetrics } from './DashboardPage.metrics';
import { AutoAnswer } from '@core/types/types.db';
import {
  DashboardCase,
  DashboardGetDataEndpoint,
  DashboardRateCaseEndpoint,
  DashboardSmartCaptureDismissEndpoint,
  DashboardSmartCaptureSaveEndpoint,
  DashboardTimeFilter,
} from '@core/types/types.endpoint.dashboard';
import { formatResolutionTime } from '@core/util/util.formatResolutionTime';
import { Alert, AlertDescription } from '@frontend/components/ui/alert';
import { Badge } from '@frontend/components/ui/badge';
import { Button } from '@frontend/components/ui/button';
import { Card, CardContent } from '@frontend/components/ui/card';
import { Checkbox } from '@frontend/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@frontend/components/ui/dropdown-menu';
import { MetricCard } from '@frontend/components/ui/metric-card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@frontend/components/ui/popover';
import { useApi } from '@frontend/shared/hooks/shared.hook.useApi';
import {
  useDirectoryChannels,
  useDirectoryUser,
  useDirectoryUsers,
} from '@frontend/shared/hooks/useDirectory';
import { callApi } from '@frontend/shared/util/util.callApi';
import {
  AlertCircle,
  ChevronDown,
  Circle,
  CircleCheck,
  CircleMinus,
  CircleX,
  HelpCircle,
  Loader2,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';

// Types for our component state
type TimeFilter = DashboardTimeFilter;

type ClassificationFilter =
  | 'all'
  | 'helpful'
  | 'unrated'
  | 'unhelpful'
  | 'no_answer';

interface DashboardFilters {
  timeFilter?: TimeFilter;
  channelIds?: string[];
  classificationFilter: ClassificationFilter;
}

// Helper function to format relative time
function formatTimeAgo(timestamp: number): string {
  const now = Date.now() / 1000;
  const diff = now - timestamp;

  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)}w ago`;
  return `${Math.floor(diff / 2592000)}mo ago`;
}

// Helper function to get classification icon
function getClassificationIcon(classification: string): React.ReactNode {
  const iconSize = 10;

  switch (classification) {
    case 'helpful':
      return (
        <CircleCheck
          size={iconSize}
          fill="#D1FAE5"
          color="#16A34A"
          strokeWidth={1.5}
        />
      );
    case 'unrated':
      return (
        <CircleMinus
          size={iconSize}
          fill="#F1F5F9"
          color="#475569"
          strokeWidth={1.5}
        />
      );
    case 'unhelpful':
      return (
        <CircleX
          size={iconSize}
          fill="#FEE2E2"
          color="#DC2626"
          strokeWidth={1.5}
        />
      );
    case 'no_answer':
      return (
        <Circle
          size={iconSize}
          fill="#7DD3FC"
          color="#0284C7"
          strokeWidth={1.5}
        />
      );
    default:
      return (
        <HelpCircle
          size={iconSize}
          fill="#DBEAFE"
          color="#2563EB"
          strokeWidth={1.5}
        />
      );
  }
}

// Helper function to truncate text
function truncateText(text: string, maxLength: number = 100): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

// Helper function to format TTR with dynamic units
function formatTTRDynamic(ttrMinutes: number): string {
  const totalSeconds = Math.max(1, Math.round(ttrMinutes * 60)); // enforce minimum 1s

  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  } else if (ttrMinutes < 60) {
    return `${Math.round(ttrMinutes)}m`;
  } else if (ttrMinutes < 1440) {
    // Less than 24 hours - show hours
    const hours = Math.round((ttrMinutes / 60) * 10) / 10; // Round to 1 decimal place
    return hours % 1 === 0 ? `${Math.round(hours)}h` : `${hours}h`;
  } else {
    // 24 hours or more - show days and hours
    // Round up to nearest hour (if there are any minutes/seconds, round up)
    const totalHours = Math.ceil(ttrMinutes / 60);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;

    if (hours === 0) {
      return `${days}d`;
    } else {
      return `${days}d ${hours}h`;
    }
  }
}

// Helper function to construct Slack URL
function constructSlackUrl(
  slackTeamId: string,
  channelId: string,
  messageTs: string,
): string {
  return `slack://channel?team=${slackTeamId}&id=${channelId}&message=${messageTs}`;
}

function slackUserUrl(slackTeamId: string, userId: string): string {
  return `slack://user?team=${slackTeamId}&id=${userId}`;
}

// Helper function to get service display name
function getServiceDisplayName(service: string): string {
  switch (service.toUpperCase()) {
    case 'QUESTIONBASE':
      return 'Question Base';
    case 'NOTION':
      return 'Notion';
    case 'GOOGLE_DRIVE':
      return 'Google Drive';
    case 'CONFLUENCE':
      return 'Confluence';
    case 'WEBCRAWLER':
      return 'Website';
    default:
      return service;
  }
}

// Helper function to get service emoji/icon
function getServiceEmoji(service: string): string {
  switch (service.toUpperCase()) {
    case 'QUESTIONBASE':
      return '📚';
    case 'NOTION':
      return '📝';
    case 'GOOGLE_DRIVE':
      return '📁';
    case 'CONFLUENCE':
      return '📖';
    default:
      return '🔗';
  }
}

// Source Links Component
interface SourceLinksProps {
  sources: AutoAnswer[];
}

function SourceLinks({ sources }: SourceLinksProps) {
  // Group sources by service
  const groupedSources = useMemo(() => {
    const groups: Record<string, AutoAnswer[]> = {};
    sources.forEach((source) => {
      if (!groups[source.service]) {
        groups[source.service] = [];
      }
      groups[source.service].push(source);
    });
    return groups;
  }, [sources]);

  if (!sources.length) return null;

  return (
    <div className="mt-4 p-3 bg-gray-50 rounded-md max-w-full overflow-hidden">
      <p className="text-sm font-medium mb-3 text-gray-700">Sources:</p>
      <div className="flex flex-col gap-3">
        {Object.entries(groupedSources).map(([service, serviceSources]) => (
          <div key={service}>
            <div className="flex items-center gap-2 mb-2">
              <p className="text-sm font-medium text-gray-600">
                {getServiceEmoji(service)} {getServiceDisplayName(service)}
              </p>
            </div>
            <div className="flex flex-col gap-1 pl-4">
              {serviceSources.map((source) => (
                <div
                  key={`${source.service}-${source.id}`}
                  className="flex items-start gap-2 min-w-0"
                >
                  <span className="text-xs text-gray-500 shrink-0 mt-0.5">
                    •
                  </span>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 underline hover:text-blue-800 leading-normal break-words min-w-0"
                  >
                    {truncateText(source.title, 100)}
                  </a>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function getTimeFilterDateRange(filter?: TimeFilter): string | null {
  if (!filter) return null;
  const match = filter.value.match(/^M(\d{1,2})_(\d{4})$/);
  if (!match) return null;
  const monthNumber = parseInt(match[1]!, 10);
  const year = parseInt(match[2]!, 10);
  const start = new Date(year, monthNumber - 1, 1);
  const end = new Date(year, monthNumber, 0);
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  return `${fmt(start)} – ${fmt(end)}`;
}

// Generate month options dynamically — current month back to Jan of the previous year.
function generateMonthOptions(): Array<{
  value: string;
  label: string;
  filterValue: TimeFilter;
}> {
  const options: Array<{
    value: string;
    label: string;
    filterValue: TimeFilter;
  }> = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-indexed

  // Include the current year's months (Jan .. current) in descending order.
  for (let m = currentMonth; m >= 0; m--) {
    const monthNumber = m + 1;
    const label = `${MONTH_NAMES[m]} ${currentYear}`;
    options.push({
      value: `month_${monthNumber}_${currentYear}`,
      label,
      filterValue: {
        type: 'month',
        value: `M${String(monthNumber).padStart(2, '0')}_${currentYear}`,
      },
    });
  }

  // Previous year, all 12 months.
  const prevYear = currentYear - 1;
  for (let m = 11; m >= 0; m--) {
    const monthNumber = m + 1;
    const label = `${MONTH_NAMES[m]} ${prevYear}`;
    options.push({
      value: `month_${monthNumber}_${prevYear}`,
      label,
      filterValue: {
        type: 'month',
        value: `M${String(monthNumber).padStart(2, '0')}_${prevYear}`,
      },
    });
  }

  return options;
}

// Question Item Component
interface QuestionItemProps {
  question: DashboardCase;
  channelName?: string;
  onRatingChange: () => void;
}

function QuestionItem({
  question,
  channelName,
  onRatingChange,
}: QuestionItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isRating, setIsRating] = useState(false);
  const [captureBusy, setCaptureBusy] = useState<{
    index: number;
    action: 'save' | 'dismiss';
  } | null>(null);

  const askerUser = useDirectoryUser(question.question_user_id);
  const firstUpvoterUser = useDirectoryUser(
    question.auto_responder_upvotes?.[0],
  );
  const firstDownvoterUser = useDirectoryUser(
    question.auto_responder_downvotes?.[0],
  );
  const respondedByFromCapture = useMemo(() => {
    if (!question.smart_capture?.length) return [];
    const seen = new Set<string>();
    return question.smart_capture
      .filter((c) => {
        if (
          c.status !== 'captured' ||
          !c.resolved_by ||
          seen.has(c.resolved_by)
        )
          return false;
        seen.add(c.resolved_by);
        return true;
      })
      .map((c) => ({ id: c.resolved_by! }));
  }, [question.smart_capture]);

  const resolvedByUser = useDirectoryUser(question.resolved_by);
  const firstRespondedByUser = useDirectoryUser(respondedByFromCapture[0]?.id);

  const savedByUserIds = useMemo(
    () =>
      question.smart_capture
        ?.filter((c) => c.status === 'captured' && c.resolved_by)
        .map((c) => c.resolved_by!) ?? [],
    [question.smart_capture],
  );
  const savedByUsers = useDirectoryUsers(savedByUserIds);
  const savedByNameMap = useMemo(() => {
    const m = new Map<string, string>();
    savedByUserIds.forEach((id, i) => {
      const u = savedByUsers[i];
      if (u?.name) m.set(id, u.name);
    });
    return m;
  }, [savedByUserIds, savedByUsers]);

  const onToggle = () => setIsOpen(!isOpen);

  const handleSmartCaptureSave = async (captureIndex: number) => {
    setCaptureBusy({ index: captureIndex, action: 'save' });
    try {
      const [error] = await callApi<DashboardSmartCaptureSaveEndpoint>(
        '/dashboard/smartCaptureSave',
        {
          caseId: question.id,
          channelId: question.channel_id,
          captureIndex,
        },
      );
      if (error) throw new Error(error.error || 'Failed to save');
      onRatingChange();
    } catch (e) {
      console.error('Save captured knowledge failed:', e);
      alert(
        e instanceof Error ? e.message : 'Failed to save. Please try again.',
      );
    } finally {
      setCaptureBusy(null);
    }
  };

  const handleSmartCaptureDismiss = async (captureIndex: number) => {
    setCaptureBusy({ index: captureIndex, action: 'dismiss' });
    try {
      const [error] = await callApi<DashboardSmartCaptureDismissEndpoint>(
        '/dashboard/smartCaptureDismiss',
        {
          caseId: question.id,
          channelId: question.channel_id,
          captureIndex,
        },
      );
      if (error) throw new Error(error.error || 'Failed to dismiss');
      onRatingChange();
    } catch (e) {
      console.error('Dismiss captured knowledge failed:', e);
      alert(
        e instanceof Error ? e.message : 'Failed to dismiss. Please try again.',
      );
    } finally {
      setCaptureBusy(null);
    }
  };

  const handleRating = async (rating: 'helpful' | 'unhelpful') => {
    setIsRating(true);
    try {
      const [error] = await callApi<DashboardRateCaseEndpoint>(
        '/dashboard/rateCase',
        {
          caseId: question.id,
          channelId: question.channel_id,
          rating,
        },
      );
      if (error) {
        throw new Error(error.error || 'Failed to rate case');
      }
      // Notify parent to refresh data
      onRatingChange();
    } catch (error) {
      console.error('Failed to rate case:', error);
      alert('Failed to rate answer. Please try again.');
    } finally {
      setIsRating(false);
    }
  };

  return (
    <Card className="py-0 gap-0 w-full overflow-hidden">
      <CardContent
        className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-all w-full"
        onClick={onToggle}
      >
        <div className="flex justify-between gap-4 w-full">
          <div className="flex gap-4 flex-1 items-center min-w-0">
            <div className="min-w-[10px] shrink-0 flex items-center justify-center">
              {getClassificationIcon(question.classification)}
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
              <p className="text-base font-medium leading-6 break-words">
                {isOpen ? question.question : truncateText(question.question)}
              </p>
              {isOpen && channelName && (
                <Badge
                  variant="default"
                  className="bg-blue-100 text-blue-700 w-fit"
                >
                  #{channelName}
                </Badge>
              )}
              {isOpen && question.classification === 'no_answer' && (
                <div className="mt-3 p-3 bg-gray-50 rounded-md max-w-full overflow-hidden border border-gray-200">
                  <p className="text-sm font-semibold text-gray-800 mb-2">
                    Activity log
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                    {question.question_user_id && (
                      <li>
                        Asked by{' '}
                        <a
                          href={slackUserUrl(
                            question.slack_team_id,
                            question.question_user_id,
                          )}
                          className="text-blue-600 hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="Open Slack profile"
                        >
                          {askerUser?.name ?? 'Unknown user'}
                        </a>
                      </li>
                    )}
                    <li>Searched across available sources</li>
                    <li>No answer was found</li>
                    <li>Escalated to team</li>
                    {question.latest_thread_ts &&
                      (!question.smart_capture ||
                        question.smart_capture.length === 0) && (
                        <li className="text-green-700 font-medium">
                          New answers available in Slack thread
                        </li>
                      )}
                    {respondedByFromCapture.length > 0 && (
                      <li>
                        Marked as resolved by{' '}
                        <a
                          href={slackUserUrl(
                            question.slack_team_id,
                            respondedByFromCapture[0].id,
                          )}
                          className="text-blue-600 hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="Open Slack profile"
                        >
                          {firstRespondedByUser?.name ?? 'Unknown user'}
                        </a>
                        {respondedByFromCapture.length > 1 && (
                          <span className="text-gray-600">
                            {' '}
                            +{respondedByFromCapture.length - 1} other
                            {respondedByFromCapture.length === 2 ? '' : 's'}
                          </span>
                        )}
                      </li>
                    )}
                    {question.resolved_by && (
                      <>
                        <li>
                          Marked as resolved by{' '}
                          <a
                            href={slackUserUrl(
                              question.slack_team_id,
                              question.resolved_by,
                            )}
                            className="text-blue-600 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title="Open Slack profile"
                          >
                            {resolvedByUser?.name ?? 'Unknown user'}
                          </a>
                        </li>
                        {(!question.smart_capture ||
                          question.smart_capture.length === 0) && (
                          <li>No answer was saved</li>
                        )}
                      </>
                    )}
                  </ul>
                </div>
              )}
              {isOpen &&
                question.classification !== 'no_answer' &&
                question.auto_responder_generated && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-md max-w-full overflow-hidden">
                    <p className="text-sm font-medium mb-2 text-gray-700">
                      Auto-generated Answer:
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed break-words">
                      {question.auto_responder_generated}
                    </p>
                  </div>
                )}
              {isOpen &&
                question.auto_responder &&
                question.auto_responder.length > 0 &&
                question.classification !== 'no_answer' && (
                  <SourceLinks sources={question.auto_responder} />
                )}
              {isOpen &&
                question.smart_capture &&
                question.smart_capture.length > 0 && (
                  <div className="mt-3 p-3 bg-[#F0F8F5] rounded-md max-w-full overflow-hidden border border-[rgba(3,52,38,0.15)]">
                    <p className="text-sm font-semibold text-[#033426] mb-3">
                      {question.classification === 'no_answer'
                        ? question.smart_capture.length === 1
                          ? 'New answer'
                          : 'New answers'
                        : 'Knowledge Detected → Save for later'}
                    </p>
                    <div className="space-y-4">
                      {question.smart_capture.map((capture, idx) => (
                        <div
                          key={idx}
                          className="text-sm text-gray-700 space-y-1"
                        >
                          {capture.status === 'captured' ? (
                            <>
                              <a
                                href={`/q/${capture.id}?team=${question.slack_team_id}`}
                                className="font-medium text-[#033426] hover:underline break-words"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {capture.question}
                              </a>
                              <div className="h-3 shrink-0" aria-hidden />
                              <p className="text-gray-600 leading-relaxed break-words whitespace-pre-wrap">
                                {capture.answer}
                              </p>
                              <div className="mt-2 pt-2 border-t border-gray-200">
                                <p className="text-xs italic text-gray-500">
                                  Saved by{' '}
                                  {capture.resolved_by ? (
                                    <a
                                      href={slackUserUrl(
                                        question.slack_team_id,
                                        capture.resolved_by,
                                      )}
                                      className="text-blue-600 hover:underline"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      title="Open Slack profile"
                                    >
                                      {savedByNameMap.get(
                                        capture.resolved_by,
                                      ) ?? 'Unknown user'}
                                    </a>
                                  ) : (
                                    'Unknown user'
                                  )}
                                </p>
                              </div>
                            </>
                          ) : (
                            <>
                              <p className="font-medium text-[#033426] break-words">
                                {capture.question}
                              </p>
                              <div className="h-3 shrink-0" aria-hidden />
                              <p className="text-gray-600 leading-relaxed break-words whitespace-pre-wrap">
                                {capture.answer}
                              </p>
                              <div className="flex gap-2 mt-2 flex-wrap">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="bg-white border-gray-300 text-black hover:bg-gray-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSmartCaptureSave(idx);
                                  }}
                                  disabled={
                                    captureBusy?.index === idx &&
                                    captureBusy?.action === 'save'
                                  }
                                >
                                  {captureBusy?.index === idx &&
                                  captureBusy?.action === 'save' ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  ) : (
                                    <span className="mr-2">📗</span>
                                  )}
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSmartCaptureDismiss(idx);
                                  }}
                                  disabled={
                                    captureBusy?.index === idx &&
                                    captureBusy?.action === 'dismiss'
                                  }
                                >
                                  {captureBusy?.index === idx &&
                                  captureBusy?.action === 'dismiss' ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  ) : null}
                                  Dismiss
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              {isOpen &&
                question.classification !== 'no_answer' &&
                (question.question_user_id ||
                  (question.classification === 'helpful' &&
                    (question.auto_responder_upvotes?.length ?? 0) > 0) ||
                  (question.classification === 'unhelpful' &&
                    (question.auto_responder_downvotes?.length ?? 0) > 0) ||
                  question.resolved_by) && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-md max-w-full overflow-hidden border border-gray-200">
                    <p className="text-sm font-semibold text-gray-800 mb-2">
                      Activity log
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                      {question.question_user_id && (
                        <li>
                          Asked by{' '}
                          <a
                            href={slackUserUrl(
                              question.slack_team_id,
                              question.question_user_id,
                            )}
                            className="text-blue-600 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title="Open Slack profile"
                          >
                            {askerUser?.name ?? 'Unknown user'}
                          </a>
                        </li>
                      )}
                      {question.classification === 'helpful' &&
                        question.auto_responder_upvotes &&
                        question.auto_responder_upvotes.length > 0 && (
                          <li>
                            Marked as helpful by{' '}
                            <a
                              href={slackUserUrl(
                                question.slack_team_id,
                                question.auto_responder_upvotes[0],
                              )}
                              className="text-blue-600 hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title="Open Slack profile"
                            >
                              {firstUpvoterUser?.name ?? 'Unknown user'}
                            </a>
                            {question.auto_responder_upvotes.length > 1 && (
                              <span className="text-gray-600">
                                {' '}
                                +{question.auto_responder_upvotes.length -
                                  1}{' '}
                                other
                                {question.auto_responder_upvotes.length === 2
                                  ? ''
                                  : 's'}
                              </span>
                            )}
                          </li>
                        )}
                      {question.classification === 'unhelpful' &&
                        question.auto_responder_downvotes &&
                        question.auto_responder_downvotes.length > 0 && (
                          <>
                            <li>
                              Marked as unhelpful by{' '}
                              <a
                                href={slackUserUrl(
                                  question.slack_team_id,
                                  question.auto_responder_downvotes[0],
                                )}
                                className="text-blue-600 hover:underline"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                title="Open Slack profile"
                              >
                                {firstDownvoterUser?.name ?? 'Unknown user'}
                              </a>
                              {question.auto_responder_downvotes.length > 1 && (
                                <span className="text-gray-600">
                                  {' '}
                                  +
                                  {question.auto_responder_downvotes.length -
                                    1}{' '}
                                  other
                                  {question.auto_responder_downvotes.length ===
                                  2
                                    ? ''
                                    : 's'}
                                </span>
                              )}
                            </li>
                            {question.auto_responder_downvote_details?.map(
                              (detail) =>
                                detail.reason && (
                                  <li key={detail.user_id}>
                                    Reason: {detail.reason}
                                  </li>
                                ),
                            )}
                          </>
                        )}
                      {question.resolved_by && (
                        <li>
                          Marked as resolved by{' '}
                          <a
                            href={slackUserUrl(
                              question.slack_team_id,
                              question.resolved_by,
                            )}
                            className="text-blue-600 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title="Open Slack profile"
                          >
                            {resolvedByUser?.name ?? 'Unknown user'}
                          </a>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              {isOpen && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {question.classification === 'unrated' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-green-300 text-green-700 hover:bg-green-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRating('helpful');
                        }}
                        disabled={isRating}
                      >
                        <ThumbsUp className="mr-2 h-4 w-4" />
                        Helpful
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRating('unhelpful');
                        }}
                        disabled={isRating}
                      >
                        <ThumbsDown className="mr-2 h-4 w-4" />
                        Unhelpful
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      const slackUrl = constructSlackUrl(
                        question.slack_team_id,
                        question.channel_id,
                        question.question_msg_ts,
                      );
                      window.open(slackUrl, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    <FaSlack className="mr-2" />
                    Open in Slack
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div
            className="flex gap-2 items-center shrink-0 self-start"
            style={{ paddingTop: '2px' }}
          >
            <div className="flex flex-col items-end gap-0.5">
              <span className="text-sm text-gray-500 whitespace-nowrap leading-6">
                {formatTimeAgo(question.asked_at)}
              </span>
              {question.time_to_resolution_minutes !== undefined && (
                <span className="text-xs text-green-600 whitespace-nowrap leading-none">
                  TTR:{' '}
                  {formatResolutionTime(
                    question.time_to_resolution_minutes,
                    true,
                  )}
                </span>
              )}
            </div>
            <div
              className="shrink-0 h-6 w-6 flex items-center justify-center"
              aria-label={isOpen ? 'Collapse' : 'Expand'}
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// MetricCard is now imported from shadcn components

// Time Filter Component
interface TimeFilterProps {
  selectedFilter?: TimeFilter;
  onFilterChange: (filter?: TimeFilter) => void;
}

function TimeFilterDropdown({
  selectedFilter,
  onFilterChange,
}: TimeFilterProps) {
  const monthOptions = useMemo(() => generateMonthOptions(), []);

  const getSelectedLabel = () => {
    if (!selectedFilter) return 'All Time';

    const option = monthOptions.find(
      (opt) =>
        selectedFilter.type === 'month' &&
        opt.filterValue.type === 'month' &&
        opt.filterValue.value === selectedFilter.value,
    );

    return option?.label || 'All Time';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="min-w-[150px] justify-between h-9 px-3 py-0 items-center"
        >
          <span className="leading-none">{getSelectedLabel()}</span>
          <ChevronDown className="h-4 w-4 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-[300px] overflow-y-auto">
        <DropdownMenuItem onClick={() => onFilterChange(undefined)}>
          All Time
        </DropdownMenuItem>
        {monthOptions.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-sm font-bold text-gray-600">
              Month
            </DropdownMenuLabel>
            {monthOptions.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => onFilterChange(option.filterValue)}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Channel Filter Component
interface ChannelFilterProps {
  selectedChannelIds?: string[];
  onChannelChange: (channelIds?: string[]) => void;
}

function ChannelFilterDropdown({
  selectedChannelIds,
  onChannelChange,
}: ChannelFilterProps) {
  const channels = useDirectoryChannels({ excludeDeleted: true });
  const [isOpen, setIsOpen] = useState(false);
  const [pendingChannelIds, setPendingChannelIds] = useState<
    string[] | undefined
  >(selectedChannelIds);
  const prevIsOpenRef = React.useRef(false);

  // Reset pending selection when dropdown opens, or update when closed
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    const justClosed = !isOpen && prevIsOpenRef.current;

    // Reset to confirmed selection when opening dropdown
    if (justOpened) {
      setPendingChannelIds(selectedChannelIds);
    }
    // Update to confirmed selection when closing dropdown (in case it changed externally)
    else if (justClosed) {
      setPendingChannelIds(selectedChannelIds);
    }

    prevIsOpenRef.current = isOpen;
  }, [isOpen, selectedChannelIds]);

  const getSelectedLabel = () => {
    // When dropdown is closed, show confirmed selection
    // When dropdown is open, show pending selection
    const idsToShow = isOpen
      ? pendingChannelIds || selectedChannelIds
      : selectedChannelIds;
    if (!idsToShow || idsToShow.length === 0) {
      return 'All channels';
    }
    if (idsToShow.length === 1) {
      const channelId = idsToShow[0];
      if (channelId === '__DM__') return 'DM';
      const channel = channels.find((c) => c.id === channelId);
      return channel ? `#${channel.name}` : 'All channels';
    }
    return `${idsToShow.length} channels selected`;
  };

  const handleChannelToggle = (channelId: string) => {
    const currentIds = pendingChannelIds || [];
    const isSelected = currentIds.includes(channelId);

    if (isSelected) {
      const newIds = currentIds.filter((id) => id !== channelId);
      setPendingChannelIds(newIds.length > 0 ? newIds : undefined);
    } else {
      setPendingChannelIds([...currentIds, channelId]);
    }
  };

  const handleConfirm = () => {
    onChannelChange(pendingChannelIds);
    setIsOpen(false);
  };

  const handleAllChannels = () => {
    onChannelChange(undefined);
    setIsOpen(false);
  };

  const isChannelSelected = (channelId: string) => {
    return pendingChannelIds?.includes(channelId) ?? false;
  };

  const hasPendingChanges = useMemo(() => {
    // Handle undefined/null cases
    const current = selectedChannelIds ?? [];
    const pending = pendingChannelIds ?? [];

    // Quick check: if both are empty, no changes
    if (current.length === 0 && pending.length === 0) return false;

    // Normalize arrays by sorting for comparison
    const currentSorted = [...current].sort();
    const pendingSorted = [...pending].sort();

    // If lengths differ, there are changes
    if (currentSorted.length !== pendingSorted.length) return true;

    // Compare sorted arrays element by element
    const hasChanges = currentSorted.some(
      (id, index) => id !== pendingSorted[index],
    );
    return hasChanges;
  }, [selectedChannelIds, pendingChannelIds]);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="min-w-[200px] justify-between h-9 px-3 py-0 items-center"
        >
          <span className="leading-none">{getSelectedLabel()}</span>
          <ChevronDown className="h-4 w-4 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-[300px] overflow-y-auto">
        <DropdownMenuItem onClick={handleAllChannels}>
          All channels
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={(e) => {
            e.preventDefault();
            handleChannelToggle('__DM__');
          }}
        >
          <div className="flex items-center gap-2 w-full">
            <Checkbox
              checked={isChannelSelected('__DM__')}
              onCheckedChange={() => {}}
              className="data-[state=checked]:bg-transparent data-[state=checked]:border-gray-400 data-[state=checked]:text-gray-700 pointer-events-none"
            />
            <span className="flex-1">DM</span>
          </div>
        </DropdownMenuItem>
        {channels.map((channel) => (
          <DropdownMenuItem
            key={channel.id}
            className="cursor-pointer"
            onSelect={(e) => {
              e.preventDefault();
              handleChannelToggle(channel.id);
            }}
          >
            <div className="flex items-center gap-2 w-full">
              <Checkbox
                checked={isChannelSelected(channel.id)}
                onCheckedChange={() => {}}
                className="data-[state=checked]:bg-transparent data-[state=checked]:border-gray-400 data-[state=checked]:text-gray-700 pointer-events-none"
              />
              <span className="flex-1">#{channel.name}</span>
            </div>
          </DropdownMenuItem>
        ))}
        {hasPendingChanges && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5">
              <Button size="sm" className="w-full" onClick={handleConfirm}>
                Confirm
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Main Dashboard Component
export function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<DashboardFilters>(() => {
    const now = new Date();
    const currentMonthValue = `M${String(now.getMonth() + 1).padStart(2, '0')}_${now.getFullYear()}`;
    return {
      classificationFilter: 'all',
      timeFilter: { type: 'month', value: currentMonthValue },
    };
  });
  const channels = useDirectoryChannels({ excludeDeleted: true });

  // Initialize filters from URL params on mount
  useEffect(() => {
    const classificationFromUrl = searchParams.get(
      'classification',
    ) as ClassificationFilter;
    const validClassifications: ClassificationFilter[] = [
      'all',
      'helpful',
      'unrated',
      'unhelpful',
      'no_answer',
    ];

    setFilters((prev) => ({
      ...prev,
      classificationFilter: validClassifications.includes(classificationFromUrl)
        ? classificationFromUrl
        : 'all',
    }));
  }, [searchParams]);

  const baseBody = useMemo(
    () => ({
      timeFilter: filters.timeFilter,
      channelIds: filters.channelIds,
    }),
    [filters.timeFilter, filters.channelIds],
  );

  // First-page request. Subsequent pages are fetched via callApi with a cursor.
  const { data, loading, error, refetch } = useApi<DashboardGetDataEndpoint>(
    '/dashboard/getDashboardData',
    baseBody,
  );

  // Accumulate cases across pages as the cursor loop progresses.
  const [allCases, setAllCases] = useState<DashboardCase[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const fetchGenRef = useRef(0);
  const baseBodyRef = useRef(baseBody);
  baseBodyRef.current = baseBody;

  // Reset accumulator + cancel in-flight cursor loop when filters change.
  useEffect(() => {
    fetchGenRef.current += 1;
    setAllCases([]);
    setIsLoadingMore(false);
  }, [baseBody]);

  // When the first page arrives, seed accumulator and loop through remaining pages.
  useEffect(() => {
    if (!data) return;

    const gen = ++fetchGenRef.current;
    setAllCases(data.cases);

    if (data.cursor === null) {
      setIsLoadingMore(false);
      return;
    }

    setIsLoadingMore(true);
    (async () => {
      let cursor: string | null = data.cursor;
      const accumulated = [...data.cases];

      while (cursor !== null) {
        if (fetchGenRef.current !== gen) return; // stale fetch

        const [err, page] = await callApi<DashboardGetDataEndpoint>(
          '/dashboard/getDashboardData',
          { ...baseBodyRef.current, cursor },
        );

        if (fetchGenRef.current !== gen) return;
        if (err || !page) break;

        accumulated.push(...page.cases);
        cursor = page.cursor;
        setAllCases([...accumulated]);
      }

      if (fetchGenRef.current === gen) {
        setIsLoadingMore(false);
      }
    })();
  }, [data]);

  // Handle filter changes
  const handleTimeFilterChange = useCallback((timeFilter?: TimeFilter) => {
    setFilters((prev) => ({ ...prev, timeFilter }));
  }, []);

  const handleChannelFilterChange = useCallback((channelIds?: string[]) => {
    setFilters((prev) => ({ ...prev, channelIds }));
  }, []);

  // Handle classification filter changes
  const handleClassificationFilterChange = useCallback(
    (classificationFilter: ClassificationFilter) => {
      setFilters((prev) => ({ ...prev, classificationFilter }));

      // Update URL params
      const newSearchParams = new URLSearchParams(searchParams);
      if (classificationFilter === 'all') {
        newSearchParams.delete('classification');
      } else {
        newSearchParams.set('classification', classificationFilter);
      }
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  // Create channel lookup map
  const channelMap = useMemo(() => {
    const map: Record<string, string> = {};
    channels.forEach((channel) => {
      map[channel.id] = channel.name;
    });
    return map;
  }, [channels]);

  const hasDMFilter = filters.channelIds?.includes('__DM__') ?? false;
  const isDMOnlyView =
    filters.channelIds?.length === 1 && filters.channelIds[0] === '__DM__';

  const metrics = useMemo(
    () => computeDashboardMetrics(allCases, hasDMFilter),
    [allCases, hasDMFilter],
  );

  // Filter + globally sort (cursor pagination across channels can interleave order).
  const filteredQuestions = useMemo(() => {
    const filtered =
      filters.classificationFilter === 'all'
        ? allCases
        : allCases.filter(
            (q) => q.classification === filters.classificationFilter,
          );
    return [...filtered].sort((a, b) => b.asked_at - a.asked_at);
  }, [allCases, filters.classificationFilter]);

  const agentResolvedCount = metrics.helpfulAnswers + metrics.unratedAnswers;
  const teamResolvedCount = metrics.unhelpfulAnswers + metrics.noAnswerFound;

  const totalAgentTimeSavedMinutes =
    metrics.totalTimeSpentAutomated !== undefined
      ? metrics.totalTimeSpentAutomated
      : metrics.averageTTRAutomated !== undefined && agentResolvedCount > 0
        ? agentResolvedCount * metrics.averageTTRAutomated
        : undefined;

  const totalTeamTimeInvestedMinutes =
    metrics.totalTimeSpentManual !== undefined
      ? metrics.totalTimeSpentManual
      : metrics.averageTTRManual !== undefined && teamResolvedCount > 0
        ? teamResolvedCount * metrics.averageTTRManual
        : undefined;

  const isInitialLoading = loading && allCases.length === 0;
  const hasNoCases =
    !loading && !isLoadingMore && allCases.length === 0 && !error;

  return (
    <div
      className="flex flex-col gap-3 w-full"
      style={{ backgroundColor: '#F9FAFB' }}
    >
      {/* Filters */}
      <Card className="py-0 gap-0 w-full">
        <CardContent className="px-4 py-3 w-full">
          <div className="flex flex-wrap gap-4 w-full items-end">
            <div>
              <p className="text-sm font-medium mb-2 text-gray-700">
                Time Period
              </p>
              <TimeFilterDropdown
                selectedFilter={filters.timeFilter}
                onFilterChange={handleTimeFilterChange}
              />
            </div>
            <div>
              <p className="text-sm font-medium mb-2 text-gray-700">Channel</p>
              <ChannelFilterDropdown
                selectedChannelIds={filters.channelIds}
                onChannelChange={handleChannelFilterChange}
              />
            </div>
            {data && !isDMOnlyView && metrics.totalQuestions > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="mb-0.5">
                    <span style={{ color: '#091F47B2' }}>✦</span>
                    Summary
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 text-sm text-gray-700 leading-relaxed">
                  The Agent handled{' '}
                  {Math.round(
                    ((metrics.helpfulAnswers + metrics.unratedAnswers) /
                      metrics.totalQuestions) *
                      100,
                  )}
                  % of questions instantly, while the team still invested{' '}
                  {metrics.totalTimeSpentManual !== undefined
                    ? formatTTRDynamic(metrics.totalTimeSpentManual)
                    : metrics.averageTTRManual
                      ? formatTTRDynamic(
                          metrics.averageTTRManual *
                            (metrics.unhelpfulAnswers + metrics.noAnswerFound),
                        )
                      : '0'}{' '}
                  on the remaining{' '}
                  {Math.round(
                    ((metrics.unhelpfulAnswers + metrics.noAnswerFound) /
                      metrics.totalQuestions) *
                      100,
                  )}
                  %.
                </PopoverContent>
              </Popover>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="w-full">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load dashboard data: {error.error || 'Unknown error'}
          </AlertDescription>
        </Alert>
      )}

      {/* Loading State — initial load and any filter-triggered refetch. */}
      {isInitialLoading && (
        <div className="flex justify-center py-8 w-full">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
        </div>
      )}

      {/* Empty state — shown instead of zero-metrics when the filter returned no cases. */}
      {hasNoCases && !isDMOnlyView && (
        <Card className="py-0 gap-0 w-full">
          <CardContent className="px-4 py-12 text-center w-full">
            <p className="text-gray-700 text-lg font-medium">
              No questions found
              {getTimeFilterDateRange(filters.timeFilter)
                ? ` for ${getTimeFilterDateRange(filters.timeFilter)}`
                : ' for the selected filters'}
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Try adjusting your time period or channel selection
            </p>
          </CardContent>
        </Card>
      )}

      {/* DM-specific view */}
      {data && !isInitialLoading && isDMOnlyView && (
        <Card className="py-0 gap-0 h-[160px]">
          <CardContent className="p-5 h-full flex flex-col">
            <div className="text-gray-600" style={{ fontSize: '13px' }}>
              DM
            </div>
            <div className="mt-auto flex justify-between items-end">
              <div className="flex flex-col min-w-0">
                <p
                  className="text-3xl font-normal leading-none mb-1 whitespace-nowrap"
                  style={{ fontSize: '32px' }}
                >
                  {metrics.dmSearchResultsCount}
                </p>
                <p
                  className="text-gray-600 whitespace-nowrap"
                  style={{ fontSize: '13px' }}
                >
                  Total Questions Asked
                </p>
              </div>
              <div className="flex flex-col min-w-0">
                <p
                  className="text-3xl font-normal leading-none mb-1 whitespace-nowrap"
                  style={{ fontSize: '32px' }}
                >
                  {metrics.dmUniqueUsers}
                </p>
                <p
                  className="text-gray-600 whitespace-nowrap"
                  style={{ fontSize: '13px' }}
                >
                  Unique users
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Automation Rate Bar */}
      {data && !isDMOnlyView && allCases.length > 0 && (
        <AutomationRateBar
          automated={metrics.helpfulAnswers + metrics.unratedAnswers}
          notAutomated={metrics.unhelpfulAnswers + metrics.noAnswerFound}
        />
      )}

      {/* Time to Resolution Metrics */}
      {data && !isDMOnlyView && allCases.length > 0 && (
        <div className="grid grid-cols-2 gap-1 w-full">
          {/* Agent Box - Always show for question channels */}
          <Card className="py-0 gap-0 h-[160px]">
            <CardContent className="p-5 h-full flex flex-col">
              <div className="text-gray-600" style={{ fontSize: '13px' }}>
                Agent
              </div>
              <div className="mt-auto flex justify-between items-end">
                <div className="flex flex-col min-w-0">
                  <p
                    className="text-3xl font-normal leading-none mb-1 whitespace-nowrap"
                    style={{ fontSize: '32px' }}
                  >
                    {metrics.averageTTRAutomated !== undefined
                      ? formatTTRDynamic(metrics.averageTTRAutomated)
                      : '—'}
                  </p>
                  <p
                    className="text-gray-600 whitespace-nowrap"
                    style={{ fontSize: '13px' }}
                  >
                    Time to resolution
                  </p>
                </div>
                <div className="flex flex-col min-w-0">
                  <p
                    className="text-3xl font-normal leading-none mb-1 whitespace-nowrap"
                    style={{ fontSize: '32px' }}
                  >
                    {totalAgentTimeSavedMinutes !== undefined
                      ? formatTTRDynamic(totalAgentTimeSavedMinutes)
                      : '—'}
                  </p>
                  <p
                    className="text-gray-600 whitespace-nowrap"
                    style={{ fontSize: '13px' }}
                  >
                    Total Time Saved
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Team Box - Always show for question channels */}
          <Card className="py-0 gap-0 h-[160px]">
            <CardContent className="p-5 h-full flex flex-col">
              <div className="text-gray-600" style={{ fontSize: '13px' }}>
                Team
              </div>
              <div className="mt-auto flex justify-between items-end">
                <div className="flex flex-col min-w-0">
                  <p
                    className="text-3xl font-normal leading-none mb-1 whitespace-nowrap"
                    style={{ fontSize: '32px' }}
                  >
                    {metrics.averageTTRManual !== undefined
                      ? formatTTRDynamic(metrics.averageTTRManual)
                      : '—'}
                  </p>
                  <p
                    className="text-gray-600 whitespace-nowrap"
                    style={{ fontSize: '13px' }}
                  >
                    Time to resolution
                  </p>
                </div>
                <div className="flex flex-col min-w-0">
                  <p
                    className="text-3xl font-normal leading-none mb-1 whitespace-nowrap"
                    style={{ fontSize: '32px' }}
                  >
                    {totalTeamTimeInvestedMinutes !== undefined
                      ? formatTTRDynamic(totalTeamTimeInvestedMinutes)
                      : '—'}
                  </p>
                  <p
                    className="text-gray-600 whitespace-nowrap"
                    style={{ fontSize: '13px' }}
                  >
                    Total Time Spent
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Questions */}
      {data && !isDMOnlyView && allCases.length > 0 && (
        <h2 className="text-xl font-semibold mt-4">Questions</h2>
      )}

      {/* Metrics */}
      {data && !isDMOnlyView && allCases.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-1 w-full">
          <MetricCard
            title={`Total Question${metrics.totalQuestions !== 1 ? 's' : ''}`}
            value={metrics.totalQuestions}
            icon={null}
            isActive={filters.classificationFilter === 'all'}
            onClick={() => handleClassificationFilterChange('all')}
          />
          <MetricCard
            title={`Helpful Answer${metrics.helpfulAnswers !== 1 ? 's' : ''}`}
            value={metrics.helpfulAnswers}
            icon={
              <CircleCheck
                size={28}
                fill="#D1FAE5"
                color="#16A34A"
                strokeWidth={1.5}
              />
            }
            isActive={filters.classificationFilter === 'helpful'}
            onClick={() => handleClassificationFilterChange('helpful')}
          />
          <MetricCard
            title={`Unrated Answer${metrics.unratedAnswers !== 1 ? 's' : ''}`}
            value={metrics.unratedAnswers}
            icon={
              <CircleMinus
                size={28}
                fill="#F1F5F9"
                color="#475569"
                strokeWidth={1.5}
              />
            }
            isActive={filters.classificationFilter === 'unrated'}
            onClick={() => handleClassificationFilterChange('unrated')}
          />
          <MetricCard
            title={`Unhelpful Answer${metrics.unhelpfulAnswers !== 1 ? 's' : ''}`}
            value={metrics.unhelpfulAnswers}
            icon={
              <CircleX
                size={28}
                fill="#FEE2E2"
                color="#DC2626"
                strokeWidth={1.5}
              />
            }
            isActive={filters.classificationFilter === 'unhelpful'}
            onClick={() => handleClassificationFilterChange('unhelpful')}
          />
          <MetricCard
            title={`New Question${metrics.noAnswerFound !== 1 ? 's' : ''}`}
            value={metrics.noAnswerFound}
            icon={
              <Circle
                size={28}
                fill="#7DD3FC"
                color="#0284C7"
                strokeWidth={1.5}
              />
            }
            isActive={filters.classificationFilter === 'no_answer'}
            onClick={() => handleClassificationFilterChange('no_answer')}
          />
        </div>
      )}

      {/* Questions List */}
      {data && !isDMOnlyView && allCases.length > 0 && (
        <div className="w-full">
          {filteredQuestions.length === 0 ? (
            <Card className="py-0 gap-0 w-full">
              <CardContent className="px-4 py-8 text-center w-full">
                <p className="text-gray-500 text-lg">
                  No questions match the classification filter
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  Try a different classification or clear the filter
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col gap-1 w-full">
              {filteredQuestions.map((question) => (
                <QuestionItem
                  key={question.id}
                  question={question}
                  channelName={channelMap[question.channel_id]}
                  onRatingChange={refetch}
                />
              ))}
              {isLoadingMore && (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
